import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Link,
    MenuItem,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import ticketService from '../services/TicketService';
import netbotLogo from '../assets/netbot-logo-wide.png';

const STATUS_OPTIONS = [
    { value: 'open', label: 'open' },
    { value: 'pending', label: 'pending' },
    { value: 'resolved', label: 'resolved' },
    { value: 'closed', label: 'closed' },
];

function normalizeStatus(value) {
    const raw = String(value || '').trim().toLowerCase();
    if (raw === 'open') return 'open';
    if (raw === 'pending') return 'pending';
    if (raw === 'resolved') return 'resolved';
    if (raw === 'close' || raw === 'closed') return 'closed';
    return raw || 'open';
}

function statusLabel(t, value) {
    switch (normalizeStatus(value)) {
        case 'open':
            return t('tickets.statusOpen', 'Open');
        case 'pending':
            return t('tickets.statusPending', 'Pending');
        case 'resolved':
            return t('tickets.statusResolved', 'Resolved');
        case 'closed':
            return t('tickets.statusClosed', 'Closed');
        default:
            return normalizeStatus(value);
    }
}

function formatEpoch(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '-';
    const ms = n < 1e12 ? n * 1000 : n;
    try {
        return new Date(ms).toLocaleString();
    } catch (e) {
        return String(value);
    }
}

function formatBytes(value) {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function getAttachmentPreviewType(attachment) {
    const contentType = String(attachment?.contentType || '').toLowerCase();
    const filename = String(attachment?.filename || '').toLowerCase();
    if (contentType.startsWith('image/') || filename.match(/\.(png|jpe?g|gif|webp)$/)) return 'image';
    if (contentType.startsWith('video/') || filename.match(/\.(mp4|mov|m4v|webm|ogv)$/)) return 'video';
    if (contentType === 'application/pdf' || filename.endsWith('.pdf')) return 'pdf';
    return '';
}

function PublicSupportTicketDetails() {
    const { t } = useTranslation();
    const { accessToken } = useParams();
    const [loading, setLoading] = useState(false);
    const [ticket, setTicket] = useState(null);
    const [error, setError] = useState('');
    const [noteError, setNoteError] = useState('');
    const [noteBody, setNoteBody] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [attachmentError, setAttachmentError] = useState('');
    const [uploadingAttachment, setUploadingAttachment] = useState(false);
    const [attachmentUpload, setAttachmentUpload] = useState({ filename: '', progress: 0 });
    const [isDraggingAttachment, setIsDraggingAttachment] = useState(false);
    const [previewAttachment, setPreviewAttachment] = useState(null);
    const attachmentDragDepthRef = useRef(0);

    useEffect(() => {
        const load = async () => {
            if (!accessToken) return;
            setLoading(true);
            setError('');
            try {
                const data = await ticketService.getPublicSupportTicket(accessToken);
                setTicket(data || null);
            } catch (e) {
                console.error('Failed to load public support ticket', e);
                setError(t('tickets.failedToLoadSupportTicket', 'Failed to load support ticket'));
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [accessToken, t]);

    const notes = Array.isArray(ticket?.notes) ? ticket.notes : [];
    const attachments = Array.isArray(ticket?.attachments) ? ticket.attachments : [];
    const status = normalizeStatus(ticket?.status);
    const previewType = getAttachmentPreviewType(previewAttachment);
    const previewUrl = previewAttachment?.previewUrl || previewAttachment?.downloadUrl || '';

    const handleAddNote = async () => {
        const trimmed = noteBody.trim();
        if (!accessToken || !trimmed) {
            setNoteError(t('tickets.noteRequired', 'Note is required'));
            return;
        }

        setAddingNote(true);
        setNoteError('');
        try {
            const updated = await ticketService.addPublicSupportTicketNote(accessToken, trimmed);
            setTicket(updated || null);
            setNoteBody('');
        } catch (e) {
            console.error('Failed to add public support ticket note', e);
            setNoteError(t('tickets.failedToAddNote', 'Failed to add note'));
        } finally {
            setAddingNote(false);
        }
    };

    const uploadAttachmentFile = async (file) => {
        if (!accessToken || !file) return;

        setUploadingAttachment(true);
        setAttachmentError('');
        setAttachmentUpload({ filename: file.name || 'attachment', progress: 0 });
        try {
            const updated = await ticketService.uploadPublicSupportTicketAttachment(accessToken, file, (progress) => {
                setAttachmentUpload({ filename: file.name || 'attachment', progress });
            });
            setAttachmentUpload({ filename: file.name || 'attachment', progress: 100 });
            setTicket(updated || null);
        } catch (e) {
            console.error('Failed to upload public support ticket attachment', e);
            setAttachmentError(t('tickets.failedToUploadAttachment', 'Failed to upload attachment'));
        } finally {
            setUploadingAttachment(false);
            setAttachmentUpload({ filename: '', progress: 0 });
            setIsDraggingAttachment(false);
            attachmentDragDepthRef.current = 0;
        }
    };

    const handleAttachmentChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (file) uploadAttachmentFile(file);
    };

    const handleAttachmentDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        attachmentDragDepthRef.current += 1;
        if (!uploadingAttachment) setIsDraggingAttachment(true);
    };

    const handleAttachmentDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    };

    const handleAttachmentDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        attachmentDragDepthRef.current = Math.max(0, attachmentDragDepthRef.current - 1);
        if (attachmentDragDepthRef.current === 0) setIsDraggingAttachment(false);
    };

    const handleAttachmentDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        attachmentDragDepthRef.current = 0;
        setIsDraggingAttachment(false);
        const file = event.dataTransfer?.files?.[0];
        if (file && !uploadingAttachment) uploadAttachmentFile(file);
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f4f1ea', py: { xs: 3, md: 6 }, px: 2 }}>
            <Box sx={{ maxWidth: 1180, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <img src={netbotLogo} alt="NetBot" style={{ height: 36, width: 'auto', objectFit: 'contain' }} />
                    <Typography variant="h5" fontWeight="bold">{t('tickets.supportTicket', 'Support Ticket')}</Typography>
                </Box>

                {loading ? (
                    <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }} elevation={1}>
                        <CircularProgress size={24} />
                        <Typography>{t('tickets.loading', 'Loading…')}</Typography>
                    </Paper>
                ) : null}

                {error ? <Alert severity="error">{error}</Alert> : null}

                {!loading && !error ? (
                    <>
                        <Paper sx={{ p: 2 }} elevation={1}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        sm: 'repeat(2, minmax(180px, 1fr))',
                                        lg: 'repeat(4, minmax(150px, 1fr))',
                                    },
                                    gap: 1,
                                    mb: 2,
                                }}
                            >
                                {[
                                    [t('tickets.supportTicketId', 'Support Ticket ID'), ticket?.supportTicketId || '-'],
                                    [t('tickets.requesterEmail', 'Requester Email'), ticket?.requesterEmail || '-'],
                                    [t('tickets.created', 'Created'), formatEpoch(ticket?.createdAt)],
                                    [t('tickets.updated', 'Updated'), formatEpoch(ticket?.updatedAt)],
                                ].map(([label, value]) => (
                                    <Box key={label} sx={{ minWidth: 0 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                            {label}
                                        </Typography>
                                        <Typography variant="body2" color="text.primary" noWrap title={String(value)}>
                                            {value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: {
                                        xs: '1fr',
                                        md: 'minmax(240px, 2fr) repeat(2, minmax(160px, 1fr))',
                                    },
                                    gap: 2,
                                    mb: 2,
                                }}
                            >
                                <TextField label={t('tickets.title', 'Title')} size="small" value={ticket?.title || ''} disabled />
                                <TextField label={t('tickets.status', 'Status')} select size="small" value={status} disabled>
                                    {STATUS_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {statusLabel(t, opt.value)}
                                        </MenuItem>
                                    ))}
                                    {!STATUS_OPTIONS.some((opt) => opt.value === status) ? (
                                        <MenuItem value={status}>{statusLabel(t, status)}</MenuItem>
                                    ) : null}
                                </TextField>
                                <TextField label={t('tickets.source', 'Source')} size="small" value={ticket?.source || '-'} disabled />
                            </Box>

                            <TextField
                                label={t('tickets.description', 'Description')}
                                fullWidth
                                size="small"
                                multiline
                                minRows={4}
                                maxRows={10}
                                value={ticket?.description || ''}
                                disabled
                            />
                        </Paper>

                        <Paper sx={{ p: 2 }} elevation={1}>
                            <Typography variant="h6" sx={{ mb: 1 }}>{t('tickets.notes', 'Notes')}</Typography>
                            {noteError ? <Alert severity="error" sx={{ mb: 1 }}>{noteError}</Alert> : null}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
                                    gap: 1.5,
                                    alignItems: 'end',
                                    mb: notes.length ? 2 : 1,
                                }}
                            >
                                <TextField
                                    label={t('tickets.addNote', 'Add Note')}
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={2}
                                    maxRows={5}
                                    value={noteBody}
                                    onChange={(e) => setNoteBody(e.target.value)}
                                    disabled={addingNote}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={handleAddNote}
                                    disabled={addingNote || !noteBody.trim()}
                                    sx={{ minWidth: 116, height: 40 }}
                                >
                                    {addingNote ? t('tickets.adding', 'Adding…') : t('tickets.addNote', 'Add Note')}
                                </Button>
                            </Box>
                            {notes.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">{t('tickets.noNotes', 'No notes.')}</Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {[...notes].sort((a, b) => Number(b?.createdAt || 0) - Number(a?.createdAt || 0)).map((note, index) => (
                                        <Box
                                            key={note?.noteId || `${note?.createdAt || ''}-${index}`}
                                            sx={{ borderLeft: '3px solid', borderColor: 'divider', pl: 1.5 }}
                                        >
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {note?.body || ''}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {[note?.createdBy || '-', formatEpoch(note?.createdAt)].filter(Boolean).join(' - ')}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Paper>

                        <Paper
                            sx={{
                                p: 2,
                                position: 'relative',
                                border: '1px solid',
                                borderColor: isDraggingAttachment ? 'primary.main' : 'transparent',
                                bgcolor: isDraggingAttachment ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
                                transition: 'border-color 120ms ease, background-color 120ms ease',
                            }}
                            elevation={1}
                            onDragEnter={handleAttachmentDragEnter}
                            onDragOver={handleAttachmentDragOver}
                            onDragLeave={handleAttachmentDragLeave}
                            onDrop={handleAttachmentDrop}
                        >
                            {isDraggingAttachment ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 8,
                                        border: '2px dashed',
                                        borderColor: 'primary.main',
                                        borderRadius: 1,
                                        bgcolor: 'rgba(255, 255, 255, 0.72)',
                                        zIndex: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        pointerEvents: 'none',
                                    }}
                                >
                                    <Typography variant="subtitle1" color="primary" fontWeight={600}>
                                        {t('tickets.dropAttachmentToUpload', 'Drop attachment to upload')}
                                    </Typography>
                                </Box>
                            ) : null}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
                                <Typography variant="h6">{t('tickets.attachments', 'Attachments')}</Typography>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    disabled={!accessToken || uploadingAttachment}
                                >
                                    {uploadingAttachment ? t('tickets.uploading', 'Uploading…') : t('tickets.addAttachment', 'Add Attachment')}
                                    <input type="file" hidden onChange={handleAttachmentChange} />
                                </Button>
                            </Box>
                            {attachmentError ? <Alert severity="error" sx={{ mb: 1.5 }}>{attachmentError}</Alert> : null}
                            {uploadingAttachment ? (
                                <Box sx={{ mb: 1.5 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary" noWrap title={attachmentUpload.filename}>
                                            {attachmentUpload.filename || t('tickets.uploadingAttachment', 'Uploading attachment')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {attachmentUpload.progress}%
                                        </Typography>
                                    </Box>
                                    <LinearProgress variant="determinate" value={attachmentUpload.progress} />
                                </Box>
                            ) : null}

                            {attachments.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">{t('tickets.noAttachments', 'No attachments.')}</Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {attachments.map((attachment, index) => {
                                        const filename = attachment?.filename || 'attachment';
                                        const meta = [
                                            formatBytes(attachment?.size),
                                            formatEpoch(attachment?.uploadedAt),
                                            attachment?.uploadedBy ? t('tickets.attachedBy', 'Attached by {{name}}', { name: attachment.uploadedBy }) : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' - ');
                                        const attachmentPreviewType = getAttachmentPreviewType(attachment);
                                        const canPreview = Boolean(attachmentPreviewType && (attachment?.previewUrl || attachment?.downloadUrl));
                                        return (
                                            <Box key={attachment?.fileId || `${filename}-${index}`}>
                                                {canPreview ? (
                                                    <Link
                                                        component="button"
                                                        type="button"
                                                        underline="hover"
                                                        onClick={() => setPreviewAttachment(attachment)}
                                                        sx={{ border: 0, p: 0, bgcolor: 'transparent', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
                                                    >
                                                        {filename}
                                                    </Link>
                                                ) : attachment?.downloadUrl ? (
                                                    <Link href={attachment.downloadUrl} target="_blank" rel="noopener noreferrer" underline="hover">
                                                        {filename}
                                                    </Link>
                                                ) : (
                                                    <Typography variant="body2">{filename}</Typography>
                                                )}
                                                {meta ? (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {meta}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Paper>
                    </>
                ) : null}

                <Dialog
                    open={Boolean(previewAttachment)}
                    onClose={() => setPreviewAttachment(null)}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{ sx: { overflow: 'hidden' } }}
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography sx={{ flex: 1, fontWeight: 600 }} noWrap>
                            {previewAttachment?.filename || t('tickets.attachmentPreview', 'Attachment Preview')}
                        </Typography>
                    </DialogTitle>
                    <DialogContent sx={{ p: 0, bgcolor: '#f5f5f5', minHeight: 360 }}>
                        {previewAttachment && previewType === 'image' ? (
                            <Box sx={{ minHeight: 360, height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                                <img
                                    src={previewUrl}
                                    alt={previewAttachment?.filename || t('tickets.attachmentPreviewAlt', 'Attachment preview')}
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                />
                            </Box>
                        ) : null}
                        {previewAttachment && previewType === 'pdf' ? (
                            <iframe
                                src={previewUrl}
                                title={previewAttachment?.filename || t('tickets.attachmentPreviewAlt', 'Attachment preview')}
                                style={{ width: '100%', height: '70vh', border: 'none', display: 'block' }}
                            />
                        ) : null}
                        {previewAttachment && previewType === 'video' ? (
                            <Box sx={{ minHeight: 360, height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                                <video
                                    src={previewUrl}
                                    controls
                                    style={{ maxWidth: '100%', maxHeight: '100%', backgroundColor: '#000' }}
                                >
                                    <track kind="captions" />
                                </video>
                            </Box>
                        ) : null}
                        {previewAttachment && !previewType ? (
                            <Box sx={{ p: 4 }}>
                                <Alert severity="info">{t('tickets.previewUnavailable', 'Preview is available for PDF, image, and video attachments.')}</Alert>
                            </Box>
                        ) : null}
                    </DialogContent>
                    <DialogActions>
                        {previewAttachment?.downloadUrl ? (
                            <Button href={previewAttachment.downloadUrl} target="_blank" rel="noopener noreferrer">
                                {t('tickets.download', 'Download')}
                            </Button>
                        ) : null}
                        <Button onClick={() => setPreviewAttachment(null)}>{t('tickets.close', 'Close')}</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Box>
    );
}

export default PublicSupportTicketDetails;
